/**
 * Pure inward helpers extracted from MaterialInward.tsx / InwardModal.tsx so the
 * scanner's HJI and legacy submit payloads and completion resolution are
 * unit-tested independently of the (deliberately not ported) station grid.
 */

export interface SlotPosition {
  x: number;
  y: number;
  z: number;
}

/** Monitored-slot key convention used by the completion watch (`x.y.z`). */
export function slotKeyDot(pos: SlotPosition): string {
  return `${pos.x}.${pos.y}.${pos.z}`;
}

export function normalizeBinId(binId?: string | null): string {
  return (binId ?? "").trim();
}

/** A dynamic/static inward gate is eligible only when enabled and inward is active. */
export function isInwardGateEligible(
  gate: { isDisabled?: boolean },
  inwardActive: boolean
): boolean {
  return inwardActive && gate?.isDisabled !== true;
}

export interface OrderSkuRow {
  order: any;
  item: any;
  itemIndex: number;
  sku: any;
  skuIndex: number;
}

/** Flatten a pending inward order's items × skus into rows (InwardModal getOrderSkuRows). */
export function getOrderSkuRows(order: any): OrderSkuRow[] {
  // Support both single item (new) and array of items (legacy)
  const items = Array.isArray(order?.inwardOrderItems)
    ? order.inwardOrderItems
    : order?.inwardOrderItem
      ? [order.inwardOrderItem]
      : [];

  return items.flatMap((item: any, itemIndex: number) =>
    (item?.inwardOrderSkus ?? []).map((sku: any, skuIndex: number) => ({
      order,
      item,
      itemIndex,
      sku,
      skuIndex,
    }))
  );
}

/** Pending orders that contain the scanned SKU (InwardModal matchingPendingOrders). */
export function findMatchingPendingOrders(
  pendingOrders: any[],
  skuId: string
): any[] {
  const trimmed = skuId.trim();
  if (!trimmed) {
    return [];
  }
  return pendingOrders.filter((order) =>
    getOrderSkuRows(order).some(({ sku }) => sku.skuId?.trim() === trimmed)
  );
}

/** Largest ordered quantity for the SKU within an order (used to cap + remainder). */
export function targetOrderQuantityForSku(
  order: any,
  skuId: string
): number | null {
  const quantities = getOrderSkuRows(order)
    .filter(({ sku }) => sku.skuId?.trim() === skuId.trim())
    .map(({ sku }) => Number(sku.quantity))
    .filter((q) => Number.isFinite(q));
  return quantities.length > 0 ? Math.max(...quantities) : null;
}

/** Remainder to split into a new order (InwardModal:277-284). Dynamic gates only. */
export function computeRemainderQuantity(
  scannedQty: number,
  targetQty: number | null,
  isStatic: boolean
): number {
  if (isStatic || targetQty === null) {
    return 0;
  }
  if (!(scannedQty > 0) || !(scannedQty < targetQty)) {
    return 0;
  }
  return targetQty - scannedQty;
}

/** POST InwardOrder body for the legacy flow (MaterialInward.tsx:838-864). */
export function buildLegacyInwardOrderPayload(params: {
  slot: SlotPosition;
  skuId: string;
  quantity: number;
  containerId: string;
  isStatic: boolean;
  binId: string;
  nowIso: string;
}) {
  const { slot, skuId, quantity, containerId, isStatic, binId, nowIso } = params;
  return {
    inwardOrderItem: {
      from: { x: slot.x, y: slot.y, z: slot.z },
      inwardOrderSkus: [
        {
          skuId,
          quantity,
          partition: "0",
          inwardedQuantity: 0,
          ...(isStatic ? { caseId: containerId } : {}),
        },
      ],
      binId,
      remarks: "",
      inwardRemarks: "",
      stagingDateTimeUtc: nowIso,
      completionState: 0,
      completionDateTimeUtc: nowIso,
    },
    isTestOrder: false,
  };
}

/** PUT InwardOrder/{ref}/binLocation body for the HJI flow (MaterialInward.tsx:1012-1030). */
export function buildHjiBinLocationPayload(params: {
  slot: SlotPosition;
  toteId: string;
  skuId: string;
  caseId: string;
  inwardedQuantity: number;
}) {
  const { slot, toteId, skuId, caseId, inwardedQuantity } = params;
  return {
    binId: toteId,
    fromLocation: { x: slot.x, y: slot.y, z: slot.z },
    skuId,
    caseId,
    inwardedQuantity,
  };
}

/** POST InwardOrder body for the remainder split (InwardModal:325-356). */
export function buildRemainderOrderPayload(params: {
  skuId: string;
  remainderQuantity: number;
  batchId?: string;
  orchestrationId?: string;
}) {
  const { skuId, remainderQuantity, batchId, orchestrationId } = params;

  const inwardOrderSku: {
    skuId: string;
    quantity: number;
    partition: string;
    batchId?: string;
  } = {
    skuId: skuId.trim(),
    quantity: remainderQuantity,
    partition: "0"
  };

  if (batchId && batchId.trim()) {
    inwardOrderSku.batchId = batchId.trim();
  }

  const payload: {
    inwardOrderItem: { inwardOrderSkus: Array<typeof inwardOrderSku> };
    orchestrationId?: string;
  } = {
    inwardOrderItem: { inwardOrderSkus: [inwardOrderSku] }
  };

  if (orchestrationId) {
    payload.orchestrationId = orchestrationId;
  }

  return payload;
}

/** Match a pending inward operator task to a placed bin + SKU (MaterialInward.tsx:885-903). */
export function inwardTaskMatchesBinSku(
  task: any,
  binId: string,
  skuId: string
): boolean {
  const inwardTask = task?.inwardTask;
  if (!inwardTask) {
    return false;
  }
  if (normalizeBinId(inwardTask.binId) !== normalizeBinId(binId)) {
    return false;
  }
  const trimmed = skuId.trim();
  return (inwardTask.binSkus ?? []).some(
    (binSku: any) => binSku?.skuId?.trim() === trimmed
  );
}

/** Map a bin id to its operatorTaskId from pending inward orders (MaterialInward.tsx:599-606). */
export function buildBinToTaskIdMap(
  pendingOrders: any[]
): Map<string, string> {
  const map = new Map<string, string>();
  pendingOrders.forEach((order: any) => {
    // Support single item or array
    const items = Array.isArray(order?.inwardOrderItems)
      ? order.inwardOrderItems
      : order?.inwardOrderItem
        ? [order.inwardOrderItem]
        : [];

    items.forEach((item: any) => {
      if (item.binId && item.operatorTaskId) {
        map.set(normalizeBinId(item.binId), item.operatorTaskId);
      }
    });
  });
  return map;
}
